import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { getLegalBases } from 'services/mapping-service';

const tableWrap = { border: '1px solid var(--vo-cloud)', borderRadius: 10, overflow: 'hidden' };
const warnTableWrap = { border: '1px solid var(--vo-treatment-share-border)', borderRadius: 10, overflow: 'hidden' };

const tableBase = { width: '100%', borderCollapse: 'collapse' };

const thBase = { fontSize: 11, fontWeight: 600, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: '.07em', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid var(--vo-cloud)' };
const thWarn = { ...thBase, color: 'var(--vo-treatment-share)', background: 'var(--vo-treatment-share-bg)', borderBottom: '1px solid var(--vo-treatment-share-border)' };
const thNormal = { ...thBase, background: 'var(--vo-mist)' };

const tdBase = { padding: '10px 14px', fontSize: 13, color: 'var(--vo-ink-2)', borderBottom: '1px solid var(--vo-mist)', verticalAlign: 'top' };
const tdPrimary = { ...tdBase, color: 'var(--vo-ink)', fontWeight: 500 };
const tdSensitive = { ...tdBase, color: 'var(--vo-danger)', fontSize: 12 };
const tdSmall = { ...tdBase, fontSize: 12 };

const SectionTitle = ({ warn, children }) => (
  <p style={{ fontSize: 13, fontWeight: 600, color: warn ? 'var(--vo-warning)' : 'var(--vo-ink-2)', margin: '0 0 12px' }}>
    {children}
  </p>
);

const Section = ({ children }) => (
  <div style={{ marginBottom: 24 }}>{children}</div>
);

const ProcessesWithoutAreaTable = ({ dados }) => {
  const { t } = useTranslation('mapping');
  return (
    <div style={warnTableWrap}>
      <table style={tableBase}>
        <thead>
          <tr><th style={thWarn}>{t('ropa.executiveSummary.processesLabel')}</th></tr>
        </thead>
        <tbody>
          {dados.map((item, i) => (
            <tr key={i}><td style={tdBase}>{item}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AreasWithoutProcessesTable = ({ dados }) => {
  const { t } = useTranslation('mapping');
  return (
    <div style={warnTableWrap}>
      <table style={tableBase}>
        <thead>
          <tr><th style={thWarn}>{t('ropa.executiveSummary.businessAreaSingle')}</th></tr>
        </thead>
        <tbody>
          {dados.map((item, i) => (
            <tr key={i}><td style={tdBase}>{item}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AreaAndProcessesTable = ({ dados }) => {
  const { t } = useTranslation('mapping');
  return (
    <div style={tableWrap}>
      <table style={tableBase}>
        <thead>
          <tr>
            <th style={thNormal}>{t('ropa.executiveSummary.areasPlural')}</th>
            <th style={thNormal}>{t('ropa.executiveSummary.processesLabel')}</th>
          </tr>
        </thead>
        <tbody>
          {(dados ?? []).map(item => (
            <tr key={`row-area-process-${item.areaName}`}>
              <td style={tdPrimary}>{item.areaName}</td>
              <td style={tdBase}>
                {(item.processes ?? []).map((s, i) => <span key={i}>{s}<br /></span>)}
              </td>
            </tr>
          ))}
          {(!dados || dados.length === 0) && (
            <tr><td style={tdBase} colSpan={2}>{t('ropa.executiveSummary.noRecords')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const AreasDetailsTable = ({ dados }) => {
  const { t, i18n } = useTranslation('mapping');
  const selectedTenant = useSelector(useAuthTenant);
  const { data: legalBasesList } = useQuery(
    ['getLegalBases', selectedTenant?.slug, i18n.language],
    async () => {
      if (!selectedTenant?.slug) return [];
      const result = await getLegalBases(selectedTenant.slug);
      return result?.data?.data ?? [];
    },
  );
  const legalBaseByKey = new Map((legalBasesList ?? []).map((lb) => [lb.key, lb.name]));
  return (
    <div style={tableWrap}>
      <table style={tableBase}>
        <thead>
          <tr>
            <th style={thNormal}>{t('ropa.executiveSummary.businessAreaSingle')}</th>
            <th style={thNormal}>{t('ropa.executiveSummary.systemsAssetsCol')}</th>
            <th style={thNormal}>{t('ropa.executiveSummary.personalDataPlural')}</th>
            <th style={thNormal}>{t('ropa.executiveSummary.sensitivePersonalDataPlural')}</th>
            <th style={thNormal}>{t('ropa.executiveSummary.legalBasesCol')}</th>
          </tr>
        </thead>
        <tbody>
          {(dados ?? []).map(item => (
            <tr key={`row-${item.areaName}`}>
              <td style={tdPrimary}>{item.areaName}</td>
              <td style={tdSmall}>{(item.assetsAndSystems ?? []).map((s, i) => <span key={i}>{s}<br /></span>)}</td>
              <td style={tdSmall}>{(item.dadosPessoais ?? []).map((s, i) => <span key={i}>{s}<br /></span>)}</td>
              <td style={tdSensitive}>{(item.dadosPessoaisSensiveis ?? []).map((s, i) => <span key={i}>{s}<br /></span>)}</td>
              <td style={tdSmall}>{(item.legalBases ?? []).map((s, i) => <span key={i}>{legalBaseByKey.get(s) ?? s}<br /></span>)}</td>
            </tr>
          ))}
          {(!dados || dados.length === 0) && (
            <tr><td style={tdBase} colSpan={5}>{t('ropa.executiveSummary.noRecords')}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const GeneralView = ({ generalView }) => {
  const { t } = useTranslation('mapping');

  const areasWithoutProcesses = generalView.areasWithoutProcesses ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {generalView.processesWithoutArea?.length > 0 && (
        <Section>
          <SectionTitle warn>{t('ropa.executiveSummary.processesWithoutArea')}</SectionTitle>
          <ProcessesWithoutAreaTable dados={generalView.processesWithoutArea} />
        </Section>
      )}

      {areasWithoutProcesses.length > 0 && (
        <Section>
          <SectionTitle warn>{t('ropa.executiveSummary.areasWithoutProcesses')}</SectionTitle>
          <AreasWithoutProcessesTable dados={areasWithoutProcesses} />
        </Section>
      )}

      <Section>
        <SectionTitle>{t('ropa.executiveSummary.areaAndProcesses')}</SectionTitle>
        <AreaAndProcessesTable dados={generalView.areaAndProcesses} />
      </Section>

      {generalView.areasDetails && (
        <Section>
          <SectionTitle>{t('ropa.executiveSummary.areaDetails')}</SectionTitle>
          <AreasDetailsTable dados={generalView.areasDetails} />
        </Section>
      )}
    </div>
  );
};
